/**
 * Pencil14 Dashboard — tiny UTM tracker for the /get-a-quote QR campaign.
 *
 * Super-admin-only page at /admin/pencil14. Pulls daily-bucketed hits from
 * GET /api/analytics/utm/summary?campaign=pencil14, plus source / medium /
 * tile-content drilldowns. No charting library — a simple ASCII-style CSS
 * spark bar is enough for a print-flyer analytics panel.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, QrCode, ExternalLink, Loader2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Pencil14Dashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const r = await axios.get(`${API}/api/analytics/utm/summary`, {
        params: { campaign: 'pencil14', days },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const maxHits = useMemo(() => {
    if (!data?.by_day?.length) return 1;
    return Math.max(...data.by_day.map(d => d.hits), 1);
  }, [data]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pencil14-dashboard">
      <header className="bg-gradient-to-br from-indigo-900 to-indigo-950 text-white px-6 py-8">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
          <QrCode className="w-7 h-7 text-amber-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">
              Pencil14 · Social Launch Attribution
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              /get-a-quote QR hits
            </h1>
            <div className="text-sm text-indigo-200 mt-1">
              Every scan of the QR baked into the 14 launch tiles + the print flyer lands here.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="bg-white/10 border border-white/30 rounded-md px-3 py-1.5 text-sm"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="pencil14-window-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button
              onClick={load}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-indigo-950 border-0"
              data-testid="pencil14-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm" data-testid="pencil14-error">
            {err}
          </div>
        )}

        {!data && !err && (
          <div className="bg-white border rounded-lg p-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
          </div>
        )}

        {data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Kpi label="Total QR hits" value={data.total} accent="text-indigo-900" testid="pencil14-kpi-hits" />
              <Kpi label="Unique visitors" value={data.unique_visitors} accent="text-amber-600" testid="pencil14-kpi-unique" />
              <Kpi label="Window" value={`${data.days} days`} accent="text-slate-700" testid="pencil14-kpi-window" />
            </div>

            {/* Daily spark bars */}
            <section className="bg-white border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-indigo-700" />
                <h2 className="font-bold text-slate-900">Daily hits</h2>
                <span className="text-xs text-slate-500">UTC dates</span>
              </div>
              {data.by_day.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">
                  No hits recorded yet in this window. Once the tiles are deployed to
                  <code className="bg-slate-100 px-1 mx-1 rounded">mosaicassessmentco.com</code>
                  and a QR is scanned, entries will show up here within seconds.
                </div>
              ) : (
                <div className="flex items-end gap-1 h-40 border-b border-slate-200 pb-1" data-testid="pencil14-daily-chart">
                  {data.by_day.map(d => (
                    <div key={d.date} className="flex-1 min-w-[6px] group relative flex flex-col items-center justify-end h-full">
                      <div
                        className="w-full bg-indigo-500 group-hover:bg-indigo-700 transition-colors rounded-t"
                        style={{ height: `${Math.max(4, (d.hits / maxHits) * 100)}%`, minHeight: '4px' }}
                        title={`${d.date}: ${d.hits} hits · ${d.unique} unique`}
                      />
                      <div className="absolute -top-6 opacity-0 group-hover:opacity-100 bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
                        {d.hits} · {d.date.slice(5)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {data.by_day.length > 0 && (
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>{data.by_day[0]?.date}</span>
                  <span>{data.by_day[data.by_day.length - 1]?.date}</span>
                </div>
              )}
            </section>

            <div className="grid md:grid-cols-3 gap-4">
              <Breakdown title="Top sources"   rows={data.by_source}  keyField="utm_source" />
              <Breakdown title="Top mediums"   rows={data.by_medium}  keyField="utm_medium" />
              <Breakdown title="Top tiles"     rows={data.by_content} keyField="utm_content"
                         hint="Set utm_content on each QR to see which day drives leads." />
            </div>

            <section className="bg-white border rounded-xl p-5 text-sm">
              <h3 className="font-bold text-slate-900 mb-2">Live QR link</h3>
              <div className="flex items-center gap-3 flex-wrap">
                <code className="bg-slate-100 px-2 py-1 rounded text-xs break-all">
                  https://mosaicassessmentco.com/get-a-quote?utm_source=social&amp;utm_medium=teaser&amp;utm_campaign=pencil14
                </code>
                <a
                  href="/social/social-teasers.html"
                  target="_blank" rel="noopener noreferrer"
                  className="text-indigo-700 hover:underline inline-flex items-center gap-1"
                >
                  Open teaser gallery <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="/social/flyer/mosaic_pencil14_flyer.pdf"
                  target="_blank" rel="noopener noreferrer"
                  className="text-indigo-700 hover:underline inline-flex items-center gap-1"
                >
                  Download lounge flyer <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="bg-white border rounded-xl px-5 py-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`text-3xl font-extrabold tracking-tight mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function Breakdown({ title, rows, keyField, hint }) {
  return (
    <section className="bg-white border rounded-xl p-5" data-testid={`pencil14-breakdown-${keyField}`}>
      <h3 className="font-bold text-slate-900 mb-3">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-slate-500 py-2">
          No data yet.{hint ? ` ${hint}` : ''}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 8).map((r, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700 truncate mr-2">
                {r[keyField] || <em className="text-slate-400">(none)</em>}
              </span>
              <span className="tabular-nums">
                <span className="text-indigo-900 font-bold">{r.hits}</span>
                <span className="text-slate-400 text-xs ml-1">· {r.unique} uniq</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
